import React, { useState, useMemo } from "react";
import { Clock, DollarSign, TrendingUp, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";

const CompactROICalculator = () => {
  const [weeklyTimeInvested, setWeeklyTimeInvested] = useState(20); // minutes
  const [timeSavedPerTask, setTimeSavedPerTask] = useState(20); // minutes
  const [valueInputType, setValueInputType] = useState("hourly"); // 'hourly' or 'salary'
  const [hourlyRate, setHourlyRate] = useState(50);
  const [annualSalary, setAnnualSalary] = useState(100000);

  const effectiveHourlyRate =
    valueInputType === "hourly" ? hourlyRate : annualSalary / 2000;

  // Calculate 52-week results using same logic as twenty.tsx
  const finalStats = useMemo(() => {
    let cumulativeTimeSaved = 0;
    let cumulativeTimeInvested = 0;
    let automatedTasks = 0;
    const totalInvestmentNeededForOneTask = 60; // 1 hour to automate a task
    let investmentProgress = 0;

    for (let week = 0; week <= 52; week++) {
      const weeklySavings = automatedTasks * timeSavedPerTask;
      cumulativeTimeSaved += weeklySavings;

      if (week > 0) {
        cumulativeTimeInvested += weeklyTimeInvested;
        investmentProgress += weeklyTimeInvested;

        if (
          totalInvestmentNeededForOneTask > 0 &&
          investmentProgress >= totalInvestmentNeededForOneTask
        ) {
          const newTasksAutomated = Math.floor(
            investmentProgress / totalInvestmentNeededForOneTask,
          );
          automatedTasks += newTasksAutomated;
          investmentProgress %= totalInvestmentNeededForOneTask;
        }
      }
    }

    const dollarValue = (cumulativeTimeSaved / 60) * effectiveHourlyRate;

    return {
      cumulativeTimeSaved: Math.round(cumulativeTimeSaved),
      cumulativeTimeInvested: Math.round(cumulativeTimeInvested),
      dollarValue: Math.round(dollarValue),
      netTimeSaved: Math.round(cumulativeTimeSaved - cumulativeTimeInvested),
    };
  }, [weeklyTimeInvested, timeSavedPerTask, effectiveHourlyRate]);

  const totalROT =
    finalStats.cumulativeTimeInvested > 0
      ? finalStats.cumulativeTimeSaved / finalStats.cumulativeTimeInvested
      : 0;

  const formatTime = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-900/20 dark:to-blue-900/20 rounded-2xl p-6 shadow-lg border border-purple-100 dark:border-purple-800">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          ⏰ Quick AI Time ROI Check
        </h3>
        <p className="text-gray-600 dark:text-gray-300 text-sm">
          See what 20 minutes a week of AI learning could be worth to you
        </p>
      </div>

      {/* Compact Input Controls */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        {/* Weekly Time Invested */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center mb-3">
            <Clock className="w-4 h-4 text-purple-500 mr-2" />
            <h4 className="font-medium text-gray-800 dark:text-white text-sm">
              Weekly Learning
            </h4>
          </div>
          <input
            type="range"
            min="5"
            max="90"
            step="5"
            value={weeklyTimeInvested}
            onChange={(e) => setWeeklyTimeInvested(Number(e.target.value))}
            className="w-full h-2 bg-purple-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="text-center mt-2">
            <span className="text-lg font-bold text-purple-600">
              {formatTime(weeklyTimeInvested)}
            </span>
          </div>
        </div>

        {/* Time Saved Per Task */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center mb-3">
            <TrendingUp className="w-4 h-4 text-green-500 mr-2" />
            <h4 className="font-medium text-gray-800 dark:text-white text-sm">
              Time Saved/Task
            </h4>
          </div>
          <input
            type="range"
            min="10"
            max="180"
            step="10"
            value={timeSavedPerTask}
            onChange={(e) => setTimeSavedPerTask(Number(e.target.value))}
            className="w-full h-2 bg-green-200 rounded-lg appearance-none cursor-pointer slider"
          />
          <div className="text-center mt-2">
            <span className="text-lg font-bold text-green-600">
              {formatTime(timeSavedPerTask)}
            </span>
          </div>
        </div>

        {/* Value of Time */}
        <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm">
          <div className="flex items-center mb-3">
            <DollarSign className="w-4 h-4 text-blue-500 mr-2" />
            <h4 className="font-medium text-gray-800 dark:text-white text-sm">
              Your Time Value
            </h4>
          </div>
          <div className="space-y-2">
            {valueInputType === "hourly" ? (
              <input
                type="range"
                min="20"
                max="200"
                step="10"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(Number(e.target.value))}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer slider"
              />
            ) : (
              <input
                type="range"
                min="40000"
                max="200000"
                step="20000"
                value={annualSalary}
                onChange={(e) => setAnnualSalary(Number(e.target.value))}
                className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer slider"
              />
            )}
            <div className="text-center">
              <span className="text-lg font-bold text-blue-600">
                {valueInputType === "hourly"
                  ? `$${hourlyRate}`
                  : `$${annualSalary.toLocaleString()}`}
              </span>
            </div>
            <div className="flex bg-gray-100 dark:bg-gray-700 rounded p-1">
              <button
                onClick={() => setValueInputType("hourly")}
                className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                  valueInputType === "hourly"
                    ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-600 dark:text-gray-300"
                }`}
              >
                Hourly
              </button>
              <button
                onClick={() => setValueInputType("salary")}
                className={`flex-1 py-1 px-2 rounded text-xs font-medium transition-colors ${
                  valueInputType === "salary"
                    ? "bg-white dark:bg-gray-600 text-blue-600 dark:text-blue-400 shadow-sm"
                    : "text-gray-600 dark:text-gray-300"
                }`}
              >
                Salary
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Results Display */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-xl p-6 text-white text-center">
        <h4 className="text-lg font-bold mb-4">Your 52-Week Results 🎯</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-xl font-bold">
              {formatTime(finalStats.cumulativeTimeInvested)}
            </div>
            <div className="text-xs opacity-90">Invested</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-xl font-bold">
              {formatTime(finalStats.cumulativeTimeSaved)}
            </div>
            <div className="text-xs opacity-90">Saved</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-xl font-bold">{totalROT.toFixed(1)}x</div>
            <div className="text-xs opacity-90">Return</div>
          </div>
          <div className="bg-white/20 rounded-lg p-3">
            <div className="text-xl font-bold">
              ${finalStats.dollarValue.toLocaleString()}
            </div>
            <div className="text-xs opacity-90">Value</div>
          </div>
        </div>

        <div className="border-t border-white/30 pt-4">
          <a
            href="/20"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block"
          >
            <Button
              variant="secondary"
              size="sm"
              className="bg-white/20 hover:bg-white/30 text-white border-white/30"
            >
              <ExternalLink className="w-4 h-4 mr-2" />
              See Full Calculator
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
};

export default CompactROICalculator;
